import React, { useState } from 'react';
import { Table, Input, Space, Card, Tag, Button, Select } from 'antd';
import { useQuery } from '@apollo/client';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {getAllCreditProducts} from '../../graphql/queries';
import './CreditProducts.css';

const { Search } = Input;
const { Option } = Select;



const CreditProducts = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [filter, setFilter] = useState({
    type: null,
    name: null,
    startDate: null,
    endDate: null,
  });

  const { loading, data, refetch } = useQuery(getAllCreditProducts, {
    variables: {
      filter: {
        type: filter.type,
        name: filter.name,
        startDate: filter.startDate,
        endDate: filter.endDate,
      },
      pagination: {
        skip: (pagination.current - 1) * pagination.pageSize,
        take: pagination.pageSize,
      },
    },
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const productTypes = [
    { value: 'ONE_TIME', label: 'One Time' },
    { value: 'YEARLY', label: 'Yearly' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'LIFETIME', label: 'Lifetime' },
    { value: 'PROMO', label: 'Promo' },
  ];

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'ONE_TIME' ? 'purple' : 'blue'}>
          {type || 'N/A'}
        </Tag>
      ),
      filters: productTypes.map(type => ({ text: type.label, value: type.value })),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => amount?.toLocaleString() || 'N/A',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => formatCurrency(price) || 'N/A',
    },
    {
      title: 'Limit',
      dataIndex: 'limit',
      key: 'limit',
      render: (limit) => limit === 0 ? 'Unlimited' : limit?.toLocaleString(),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDate(date),
    },
  ];

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(newPagination);
    // You can add sorting and filtering logic here if needed
  };

  const handleSearch = (value) => {
    setFilter(prev => ({ ...prev, name: value || null }));
    setPagination({ ...pagination, current: 1 });
  };

  const handleTypeChange = (value) => {
    setFilter(prev => ({ ...prev, type: value || null }));
    setPagination({ ...pagination, current: 1 });
  };

  return (
    <div className="credit-products-container">
      <Card>
        <div className="table-header">
          <h2>Credit Products</h2>
          <div className="header-actions">
            <Space size="middle">
              <Select
                placeholder="Filter by type"
                allowClear
                style={{ width: 180 }}
                onChange={handleTypeChange}
              >
                {productTypes.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
              <Input
                placeholder="Search by name"
                allowClear
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                style={{ width: 300 }}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={(e) => handleSearch(e.target.value)}
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => navigate('/credit-products/create')}
                style={{ 
                  height: '40px',
                  padding: '0 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                Create Product
              </Button>
            </Space>
          </div>
        </div>
        
        <Table
          columns={columns}
          dataSource={data?.getAllCreditProducts?.items}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            total: data?.getAllCreditProducts?.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} credit products`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => navigate(`/credit-products/${record.id}`),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>
    </div>
  );
};

export default CreditProducts; 